// DAEMON — the codex.
//
// The reference screen the pool needed from the day it was written.
// `byArchetype` in `upgrades.ts` has carried the comment "For the
// collection screen" since the pool landed, and for that whole time
// nothing called it: twenty-eight cards were reachable in-game and
// none of them were readable anywhere. You met a card once, mid-fight,
// with a bullet pattern demanding your attention, and then it was gone
// until the draw offered it again.
//
// That is a bad deal for a system whose entire premise is that you are
// building something deliberate. So: every card, grouped by archetype,
// with the draft's actual rules stated at the top rather than left to
// be inferred over twenty runs.
//
// Two things it is careful about:
//
//   1. **Synergy links are shown as links, not as prose.** A card that
//      reads stronger when you hold another one is the mechanic; the
//      `synergy` list is only a draft-weighting hint. Both are shown,
//      and they are labelled differently, because conflating them is
//      how a player ends up drafting for a bonus that does not exist.
//
//   2. **It never claims to know your run.** `held` is optional. Given
//      one it marks what you hold; without one it is a plain catalogue
//      and says nothing about you.
//
// A note on the word: DAEMON's *cards* are its attacks — the bullet-hell
// convention, each with a name, a pattern and a timer. The things in
// here are **upgrades**. Both were called "cards" for a while and it
// confused exactly the person you would expect it to confuse, so the
// player-facing copy now keeps them apart even though `UpgradeDef` is
// still rendered as a card-shaped box.
//
// Pure presentation. No fetches, no storage, no game state — safe to
// open mid-run, and it ships in both entries, so it imports nothing
// from `src/shell`, `src/apps` or `src/lib/wm`.
//
// Styling: prefix `dcx-`, in the "DAEMON — codex" section of daemon.css.

import { useEffect, useMemo, useState } from "react";
import {
  activeLinks,
  OFFERS_PER_DRAFT,
  PICKS_PER_RUN,
  poolByArchetype,
  POOL_SIZE,
  upgradeById,
  type ArchetypeInfo,
  type UpgradeDef,
} from "./upgrades";
import type { UpgradeArchetype } from "./types";

/** Number of distinct four-card hands, stated rather than computed, so
 *  the manual, the codex and the source cannot drift apart. C(28,4). */
const HANDS = 20475;

type Filter = "all" | UpgradeArchetype;

export function UpgradeCodex({
  held = [],
  onClose,
}: {
  /** What the player is currently holding, if a run is in progress. */
  held?: string[];
  onClose?: () => void;
}): JSX.Element {
  const [filter, setFilter] = useState<Filter>("all");
  const groups = useMemo(() => poolByArchetype(), []);
  const shown = filter === "all" ? groups : groups.filter((g) => g.info.id === filter);

  useEffect(() => {
    if (!onClose) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const heldSet = new Set(held);

  return (
    <div className="dcx">
      <header className="dcx-head">
        <div className="dcx-title">
          <h2>the upgrade pool</h2>
          <p>
            {POOL_SIZE} upgrades. You draft {PICKS_PER_RUN} of them per run, {OFFERS_PER_DRAFT}{" "}
            offered at a time, once after each of the first {PICKS_PER_RUN} runlevels fall.{" "}
            <b>{HANDS.toLocaleString()}</b> possible hands.
          </p>
        </div>
        {onClose ? (
          <button type="button" className="dcx-close" onClick={onClose}>
            close <kbd>Esc</kbd>
          </button>
        ) : null}
      </header>

      {/* The rule nobody could have worked out unaided: the draft leans
          toward the build you already have, but leaning is *all* it
          does. The power is in the cards' own conditions. */}
      <p className="dcx-rule">
        The draw is weighted toward what you already hold — by archetype, and by the synergy links
        below. That decides <em>what you are offered</em>, and nothing else: stacking an archetype
        does not itself make you stronger. The power is in the cards that read differently when you
        hold a particular other card, and those are marked.
      </p>

      <div className="dcx-filters" role="tablist" aria-label="archetype">
        <button
          type="button"
          role="tab"
          aria-selected={filter === "all"}
          className="dcx-filter"
          data-on={filter === "all" ? "yes" : "no"}
          onClick={() => setFilter("all")}
        >
          all <span className="dcx-count">{POOL_SIZE}</span>
        </button>
        {groups.map((g) => (
          <button
            key={g.info.id}
            type="button"
            role="tab"
            aria-selected={filter === g.info.id}
            className="dcx-filter"
            data-arch={g.info.id}
            data-on={filter === g.info.id ? "yes" : "no"}
            onClick={() => setFilter(g.info.id)}
          >
            {g.info.name} <span className="dcx-count">{g.cards.length}</span>
          </button>
        ))}
      </div>

      <div className="dcx-scroll">
        {shown.map((g) => (
          <Group key={g.info.id} info={g.info} cards={g.cards} held={held} heldSet={heldSet} />
        ))}
      </div>
    </div>
  );
}

function Group({
  info,
  cards,
  held,
  heldSet,
}: {
  info: ArchetypeInfo;
  cards: UpgradeDef[];
  held: string[];
  heldSet: Set<string>;
}): JSX.Element {
  return (
    <section className="dcx-group" data-arch={info.id}>
      <h3 className="dcx-group-name">
        {info.name} <span className="dcx-count">{cards.length}</span>
      </h3>
      <p className="dcx-group-blurb">{info.blurb}</p>
      <div className="dcx-cards">
        {cards.map((u) => (
          <Card key={u.id} u={u} held={held} owned={heldSet.has(u.id)} />
        ))}
      </div>
    </section>
  );
}

function Card({
  u,
  held,
  owned,
}: {
  u: UpgradeDef;
  held: string[];
  owned: boolean;
}): JSX.Element {
  // Two different relationships, deliberately not merged. `live` are
  // conditions actually satisfied right now — real arithmetic. `pairs`
  // are authored affinities that only tilt the draw.
  const live = owned ? activeLinks(u.id, held) : [];
  const pairs = u.synergy;

  return (
    <article
      className="dcx-card"
      data-arch={u.archetype}
      data-rarity={u.rarity}
      data-owned={owned ? "yes" : "no"}
    >
      <header className="dcx-card-head">
        <b className="dcx-card-name">{u.name}</b>
        <span className="dcx-card-rarity">{u.rarity}</span>
      </header>
      <p className="dcx-card-blurb">{u.blurb}</p>
      {live.length > 0 ? (
        <p className="dcx-link dcx-link--live">
          <i aria-hidden="true" /> stronger right now, because you hold{" "}
          {live.map((id) => upgradeById(id)?.name ?? id).join(", ")}
        </p>
      ) : null}
      {pairs.length > 0 ? (
        <p className="dcx-link dcx-link--pair">
          drafts alongside {pairs.map((id) => upgradeById(id)?.name ?? id).join(", ")}
        </p>
      ) : null}
      {owned ? <span className="dcx-owned">held</span> : null}
    </article>
  );
}
